import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useLazyQuery } from '@apollo/client'
import {
  Box,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import type { Schema } from '@pkg/zero'
import { useZero, useQuery as useZeroQuery } from '@rocicorp/zero/react'
import type { DayObject } from 'types/graphql'

import MultiObjectChip from 'src/components/Chips/MultiObjectChip/MultiObjectChip'
import ObjectTile from 'src/components/ObjectTile/ObjectTile'
import Row from 'src/components/Row/Row'
import { DayContext } from 'src/lib/dayContext'
import { logger } from 'src/lib/logger'
import {
  NativeObjectTypes,
  ObjectTypeMetadata,
  type NativeObjectType,
} from 'src/lib/objects'

import { GET_WORKSPACE_OBJECTS_BY_IDS } from '../queries'
import useObject from '../useObject'

import SidebarHeader from './SidebarHeader'
import SidebarTabs from './SidebarTabs'

// Style constants outside component
const containerSx = {
  height: '100%',
  overflow: 'hidden',

  '& .objects-results-content': {
    height: '100%',
    overflowY: 'auto',
  },
}

const contentInnerSx = {
  px: 3,
  height: '100%', // Account for header and tabs
  overflowY: 'hidden',
}

const objectTypeHeaderSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  mb: 1.5,
  mt: 2,
}

const objectTileContainerSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
  mb: 3,
}

const MAX_COLLAPSED_ITEMS = 3

const SidebarModeRelated = () => {
  const { object, workspaceId } = useObject()
  const { setSidebarObject } = useContext(DayContext)
  const [_relatedObjectsByType, setRelatedObjectsByType] = useState<
    Record<string, DayObject[]>
  >({})
  const [selectedTypes, setSelectedTypes] = useState<Set<NativeObjectType>>(
    new Set()
  )
  const [relatedObjectsLoading, setRelatedObjectsLoading] = useState(false)
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set())

  const [getObjects] = useLazyQuery(GET_WORKSPACE_OBJECTS_BY_IDS)

  const logMeta = useMemo(
    () => ({
      component: 'SidebarModeRelated',
      objectType: object?.objectType,
      objectId: object?.objectId,
      workspaceId,
    }),
    [object, workspaceId]
  )

  const _objectsFetched = useRef(false)

  const [_objectIdsByType, _setObjectIdsByType] = useState({})

  // Zero client for querying relationships
  const z = useZero<Schema>()

  // Query for related objects using Zero
  const [relatedObjectsZero, zeroMeta] = useZeroQuery(
    z.query.objectRelationshipProperty.where(({ cmp, and, not }) =>
      and(
        cmp('workspaceId', workspaceId),
        cmp('sourceObjectTypeId', object?.objectType || ''),
        cmp('sourceObjectId', '=', object?.objectId || ''),
        cmp('name', '=', 'exists'),
        not(cmp('propertyTypeId', 'deletemarker'))
      )
    ),
    {
      enabled: !!object && !!workspaceId,
    }
  )

  // Extract and group related object IDs by type
  const groupedRelatedObjectIds = useMemo(() => {
    if (!relatedObjectsZero || relatedObjectsZero.length === 0) {
      logger.info('No related objects from Zero query', logMeta)
      return {}
    }

    const groups: Record<string, string[]> = {}

    for (const relationship of relatedObjectsZero as any) {
      const targetType = relationship.targetObjectTypeId
      const targetId = relationship.targetObjectId

      if (targetType && targetId) {
        if (!groups[targetType]) {
          groups[targetType] = []
        }
        if (!groups[targetType].includes(targetId)) {
          groups[targetType].push(targetId)
        }
      }
    }

    logger.info('Grouped related object IDs:', {
      ...logMeta,
      groups,
      totalTypes: Object.keys(groups).length,
      totalObjects: Object.values(groups).reduce(
        (sum, ids) => sum + ids.length,
        0
      ),
    })

    return groups
  }, [relatedObjectsZero, logMeta])

  // Get all unique object types from relationships
  const availableObjectTypes = useMemo(() => {
    return Object.keys(groupedRelatedObjectIds) as NativeObjectType[]
  }, [groupedRelatedObjectIds])

  // Filter related objects based on selected types
  const filteredRelatedObjects = useMemo(() => {
    logger.info('Filtering objects:', {
      selectedTypesSize: selectedTypes.size,
      selectedTypes: Array.from(selectedTypes),
      availableTypes: Object.keys(_relatedObjectsByType),
      hasUserContext: !!_relatedObjectsByType[NativeObjectTypes.UserContext],
    })

    if (selectedTypes.size === 0) {
      logger.info('No types selected, returning all objects')
      return _relatedObjectsByType
    }

    const filtered: Record<string, DayObject[]> = {}
    for (const type of selectedTypes) {
      if (_relatedObjectsByType[type]) {
        filtered[type] = [..._relatedObjectsByType[type]].sort((a, b) => {
          const aDate = new Date(a.updatedAt)
          const bDate = new Date(b.updatedAt)
          return bDate.getTime() - aDate.getTime()
        })
      }
    }

    logger.info('Filtered result:', {
      filteredTypes: Object.keys(filtered),
      counts: Object.entries(filtered).map(([k, v]) => ({ [k]: v.length })),
    })

    return filtered
  }, [_relatedObjectsByType, selectedTypes])

  // Fetch full object data when related object IDs change
  useEffect(() => {
    const fetchRelatedObjects = async () => {
      if (Object.keys(groupedRelatedObjectIds).length === 0) {
        logger.info('No grouped related object IDs to fetch', logMeta)
        setRelatedObjectsByType({})
        return
      }

      logger.info('Fetching related objects:', {
        ...logMeta,
        groupedRelatedObjectIds,
        typeCount: Object.keys(groupedRelatedObjectIds).length,
      })

      setRelatedObjectsLoading(true)

      try {
        const results = await Promise.all(
          Object.entries(groupedRelatedObjectIds).map(
            ([objectType, objectIds]) => {
              logger.info('Fetching objects for type:', {
                ...logMeta,
                objectType,
                objectIds,
                count: objectIds.length,
              })
              return getObjects({
                variables: {
                  objectType,
                  workspaceId,
                  objectIds,
                },
              })
            }
          )
        )

        const newRelatedObjectsByType: Record<string, DayObject[]> = {}

        results.forEach((result, index) => {
          const objectType = Object.keys(groupedRelatedObjectIds)[index]
          logger.info('GraphQL result for type:', {
            ...logMeta,
            objectType,
            hasData: !!result.data,
            dataStructure: result.data ? Object.keys(result.data) : null,
            workspaceObjectsByIds: result.data?.workspaceObjectsByIds,
          })

          if (result.data?.workspaceObjectsByIds) {
            newRelatedObjectsByType[objectType] =
              result.data.workspaceObjectsByIds
          }
        })

        logger.info('Final related objects by type:', {
          ...logMeta,
          newRelatedObjectsByType,
          typeCount: Object.keys(newRelatedObjectsByType).length,
        })

        setRelatedObjectsByType(newRelatedObjectsByType)
      } catch (error) {
        logger.error('Error fetching related objects:', {
          ...logMeta,
          message: error.message,
          stack: error.stack,
        })
      } finally {
        setRelatedObjectsLoading(false)
      }
    }

    fetchRelatedObjects()
  }, [groupedRelatedObjectIds, getObjects, workspaceId, logMeta])

  // Handle related object click
  const handleRelatedObjectClick = useCallback(
    (objectType: string, objectId: string) => {
      setSidebarObject({
        objectType: objectType as NativeObjectType,
        objectId,
        workspaceId,
      })
    },
    [setSidebarObject, workspaceId]
  )

  return (
    object && (
      <Box
        className="sidebar-content-mode-related"
        sx={containerSx}
      >
        <SidebarHeader />
        <SidebarTabs />

        <Box
          className="section"
          sx={{ height: '100%' }}
        >
          <Box
            className="sidebar-content-mode-related-inner"
            sx={contentInnerSx}
          >
            {relatedObjectsLoading && availableObjectTypes.length === 0 ? (
              <Row sx={{ justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Row>
            ) : (
              <>
                {/* Related Objects Section - now the main section */}
                {(availableObjectTypes.length > 0 ||
                  (zeroMeta && relatedObjectsZero?.length === 0)) && (
                  <Box sx={{ height: '100%' }}>
                    {/* Filter toggle buttons - only show if more than one type */}
                    {availableObjectTypes.length > 1 && (
                      <Row
                        className="section-header-row"
                        sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
                      >
                        <ToggleButtonGroup
                          className="object-types-toggle-button-group"
                          value={
                            selectedTypes.size === 0
                              ? ['all']
                              : Array.from(selectedTypes)
                          }
                          exclusive={false}
                        >
                          <ToggleButton
                            value="all"
                            onClick={() => {
                              logger.info('All button clicked')
                              setSelectedTypes(new Set())
                            }}
                          >
                            All
                          </ToggleButton>
                          {(availableObjectTypes ?? []).map((type) => {
                            const metadata = ObjectTypeMetadata[type]
                            return (
                              metadata && (
                                <ToggleButton
                                  key={type}
                                  value={type}
                                  onClick={() => {
                                    logger.info('Type button clicked:', {
                                      type,
                                      currentlySelected:
                                        selectedTypes.has(type),
                                    })
                                    setSelectedTypes((prev) => {
                                      const newSet = new Set(prev)
                                      if (newSet.has(type)) {
                                        newSet.delete(type)
                                      } else {
                                        newSet.add(type)
                                      }
                                      return newSet
                                    })
                                  }}
                                >
                                  {type === NativeObjectTypes.UserContext
                                    ? 'Context'
                                    : metadata?.label || type}
                                </ToggleButton>
                              )
                            )
                          })}
                        </ToggleButtonGroup>
                      </Row>
                    )}

                    {/* Related objects list */}
                    {Object.keys(filteredRelatedObjects).length > 0 ? (
                      <Box className="objects-results-content">
                        {Object.entries(filteredRelatedObjects).map(
                          ([objectType, objects]) => {
                            const metadata = ObjectTypeMetadata[objectType]
                            const Icon = metadata?.icon
                            const isExpanded = expandedTypes.has(objectType)
                            const shouldShowExpandButton =
                              availableObjectTypes.length > 1 &&
                              objects.length > MAX_COLLAPSED_ITEMS
                            const displayedObjects =
                              availableObjectTypes.length === 1 || isExpanded
                                ? objects
                                : objects.slice(0, MAX_COLLAPSED_ITEMS)

                            // Regular object types
                            return (
                              <Box key={objectType}>
                                <Box sx={objectTypeHeaderSx}>
                                  {Icon && <Icon size={16} />}
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight={600}
                                  >
                                    {metadata?.pluralLabel || objectType} (
                                    {objects.length})
                                  </Typography>
                                </Box>

                                <Box sx={objectTileContainerSx}>
                                  {displayedObjects.map((obj) => (
                                    <ObjectTile
                                      key={obj.objectId}
                                      objectType={objectType}
                                      objectId={obj.objectId}
                                      workspaceId={workspaceId}
                                      properties={obj}
                                      onClick={() =>
                                        handleRelatedObjectClick(
                                          objectType,
                                          obj.objectId
                                        )
                                      }
                                    />
                                  ))}
                                  {shouldShowExpandButton && (
                                    <Box sx={{ pb: 2 }}>
                                      <MultiObjectChip
                                        crmObjects={objects
                                          .slice(MAX_COLLAPSED_ITEMS)
                                          .map((obj) => ({
                                            objectId: obj.objectId,
                                            objectType: objectType,
                                            properties: obj,
                                          }))}
                                        workspaceId={workspaceId}
                                        onClick={() => {
                                          setExpandedTypes((prev) => {
                                            const newSet = new Set(prev)
                                            if (isExpanded) {
                                              newSet.delete(objectType)
                                            } else {
                                              newSet.add(objectType)
                                            }
                                            return newSet
                                          })
                                        }}
                                        label={
                                          isExpanded
                                            ? 'Show less'
                                            : `View all ${objects.length} ${metadata?.pluralLabel || objectType}`
                                        }
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>
                            )
                          }
                        )}
                      </Box>
                    ) : availableObjectTypes.length > 0 &&
                      selectedTypes.size > 0 ? (
                      <Typography
                        variant="body2"
                        sx={{ textAlign: 'center', opacity: 0.6, mt: 4 }}
                      >
                        No objects of selected types
                      </Typography>
                    ) : zeroMeta && relatedObjectsZero?.length === 0 ? (
                      <Typography
                        variant="body2"
                        sx={{ textAlign: 'center', opacity: 0.6, mt: 4 }}
                      >
                        No related objects found
                      </Typography>
                    ) : null}
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    )
  )
}

export default SidebarModeRelated
